import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCategory } from '../utils/categories';
import ChartFrame from './ChartFrame';

interface DataPoint {
  category: string;
  amount: number;
}

interface Props {
  data: DataPoint[];
}

const COLORS = [
  '#667eea', '#764ba2', '#f5576c', '#4facfe',
  '#43e97b', '#fa709a', '#fee140', '#a18cd1',
];

const CategorySpendingChart: React.FC<Props> = ({ data: rawData }) => {
  const [showRaw, setShowRaw] = useState(false);
  const data = rawData.map(d => ({ ...d, category: formatCategory(d.category) }));
  const total = data.reduce((sum, d) => sum + d.amount, 0);

  if (!data.length) {
    return (
      <div className="chart-wrapper">
        <p className="chart-empty">No spending data available.</p>
      </div>
    );
  }

  const renderChart = (fullscreen: boolean) => (
    <ResponsiveContainer width="100%" height={fullscreen ? '100%' : 280}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius="50%"
          outerRadius="75%"
          dataKey="amount"
          nameKey="category"
          paddingAngle={2}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number, name: string) => [`$${value.toFixed(2)}`, name]} />
        <Legend iconType="circle" iconSize={10} />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderTable = () => (
    <table className="chart-data-table">
      <thead>
        <tr><th>Category</th><th>Amount</th><th>Share</th></tr>
      </thead>
      <tbody>
        {data.map((d, i) => (
          <tr key={i}>
            <td>
              <span className="chart-table-dot" style={{ background: COLORS[i % COLORS.length] }} />
              {d.category}
            </td>
            <td>${d.amount.toFixed(2)}</td>
            <td>{total > 0 ? ((d.amount / total) * 100).toFixed(1) : '0'}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <ChartFrame
      title="Spending by Category"
      showRaw={showRaw}
      onToggle={setShowRaw}
      renderChart={renderChart}
      renderTable={renderTable}
    />
  );
};

export default CategorySpendingChart;
